import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import NumberInput from '@rio-cloud/rio-uikit/NumberInput';

import type { RangeValue } from '@/features/brakes/hooks/useBrakesQueryParams';
import { formatRange } from '@/features/common/components/RangeInputWithSlider';

interface RangeInputDropdownProps {
    /** Column label shown above the dropdown. */
    title: string;
    /** Current committed lower bound (undefined = no lower bound). */
    fromValue?: number;
    /** Current committed upper bound (undefined = no upper bound). */
    toValue?: number;
    /** Lower limit of the inputs. */
    min: number;
    /** Upper limit of the inputs. */
    max: number;
    /** Increment/decrement step. */
    step?: number;
    /** Unit suffix rendered in the trigger label and inputs (e.g. "km"). */
    unit?: string;
    /**
     * Called when the user applies a range. Bounds equal to [min]/[max] are
     * collapsed to undefined so a full range means "no filter".
     */
    onApply: (range: RangeValue) => void;
    disabled?: boolean;
}

/**
 * A dropdown range control with two number inputs and explicit Apply/Cancel.
 * Unlike RangeInputWithSlider, the value is only committed on Apply.
 */
const RangeInputDropdown: React.FC<RangeInputDropdownProps> = ({
    title,
    fromValue,
    toValue,
    min,
    max,
    step = 1,
    unit = '',
    onApply,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [fromDraft, setFromDraft] = useState<number>(fromValue ?? min);
    const [toDraft, setToDraft] = useState<number>(toValue ?? max);

    // Re-seed drafts from committed values whenever they change (e.g. settings hydration or reset).
    useEffect(() => {
        setFromDraft(fromValue ?? min);
        setToDraft(toValue ?? max);
    }, [fromValue, toValue, min, max]);

    const triggerLabel =
        typeof fromValue === 'number' || typeof toValue === 'number' ? (
            <span>{formatRange({ from: fromValue, to: toValue }, unit)}</span>
        ) : (
            <span className="placeholder">
                <FormattedMessage id="intl-msg:vehicle-diagnostics.maintenance.filter.placeholder" />
            </span>
        );

    const resetDrafts = () => {
        setFromDraft(fromValue ?? min);
        setToDraft(toValue ?? max);
    };

    const close = () => {
        setIsOpen(false);
    };

    const handleApply = () => {
        // Collapse full-range bounds to undefined so "whole range" means "no filter".
        onApply({
            from: fromDraft <= min ? undefined : fromDraft,
            to: toDraft >= max ? undefined : toDraft,
        });
        close();
    };

    const handleCancel = () => {
        resetDrafts();
        close();
    };

    const isValid = fromDraft <= toDraft;

    return (
        <div className="display-flex flex-column min-width-200">
            <div className="display-flex align-content-start gap-10">
                <span className="text-color-highlight-light margin-bottom-5">
                    <FormattedMessage id={title} />
                </span>
            </div>
            <ButtonDropdown
                className="range-input-dropdown align-top select"
                toggleClassName="no-hover-scale"
                dropdownClassName="width-250"
                title={triggerLabel}
                open={isOpen}
                disabled={disabled}
                onOpen={() => setIsOpen(true)}
                onClose={close}
                customDropdown={
                    <>
                        <li className="range padding-15">
                            <div className="display-grid gap-10 grid-cols-2">
                                <NumberInput
                                    min={min}
                                    max={max}
                                    step={step}
                                    value={fromDraft}
                                    unit={unit || undefined}
                                    onChange={(value) => setFromDraft(value ?? min)}
                                />
                                <NumberInput
                                    min={min}
                                    max={max}
                                    step={step}
                                    value={toDraft}
                                    unit={unit || undefined}
                                    onChange={(value) => setToDraft(value ?? max)}
                                />
                            </div>
                        </li>
                        <li className="btn-toolbar padding-x-15 padding-bottom-15">
                            <button
                                type="button"
                                className="btn btn-primary pull-right"
                                disabled={!isValid}
                                onClick={handleApply}
                            >
                                <FormattedMessage id="intl-msg:vehicle-diagnostics.button.apply" />
                            </button>
                            <button type="button" className="btn btn-default pull-right" onClick={handleCancel}>
                                <FormattedMessage id="intl-msg:vehicle-diagnostics.button.cancel" />
                            </button>
                        </li>
                    </>
                }
            />
        </div>
    );
};

export default RangeInputDropdown;
